<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-flex tw-justify-between tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">
          {{ pageTitle }}
        </h1>
        <div v-if="hideCreation === false">
          <base-dropdown
            :options="['Create Plan', 'Send Invite']"
            :showBackground="false"
            color="primary"
            type="primary"
            v-if="organization.operatingModel === 'coordinator'"
            align="right"
          >
            <base-button color="primary" type="primary">
              <div class="tw-flex tw-items-center tw-cursor-pointer">
                <div>{{ newMemberButtonTitle }}</div>
                <chevron-down-icon class="tw-h-5 tw-w-5 tw-ml-2" />
              </div>
            </base-button>

            <template #option="{ option: link }">
              <button
                :disabled="!organization.isActive"
                class="tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 focus:tw-outline-none"
                @click="link === 'Create Plan' ? createMemberPlan() : inviteMemberPlan()"
              >
                {{ link }}
              </button>
            </template>
          </base-dropdown>
          <base-button v-else type="primary" color="primary" @click="createMemberPlan()" :disabled="!organization.isActive">
            {{ newMemberButtonTitle }}
          </base-button>
        </div>
      </base-content>
    </div>
    <base-content>
      <base-text-field v-model="query" ref="searchInput" class="w-full tw-mb-6" :placeholder="searchPlaceholder" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <div id="filter" class="tw-flex tw-justify-end">
        <base-button @click="$refs.filterSlideOver.show()" class="tw-mt-3 tw-mb-2 tw-pr-0" color="primary" size="sm" type="flat">
          <template v-slot:icon>
            <filter-icon />
          </template>
          Filters<base-badge class="tw-ml-2" color="primary" size="sm">{{ countFilters }}</base-badge>
        </base-button>
        <export-button
          class="tw-mt-3 tw-mb-2 tw-pr-0"
          size="sm"
          type="primary"
          label="Export"
          :filename="`PredictileBusinessMembers.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/members/export?query=${encodeURIComponent(this.query || '')}`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
      <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-2">
        <template #avatar="{ item: member }">
          <div class="tw-h-12 tw-w-12">
            <Avatar :userId="member.userId" avatarType="user" />
          </div>
        </template>
        <template #member="{ item: member }">
          <div class="tw-flex">
            <base-button type="text" size="lg" @click="view(member.userId)">
              <span class="tw-whitespace-nowrap tw-text-left hover:tw-underline">{{ member.fullName }}</span>
            </base-button>
            <base-badge class="tw-ml-3 tw-my-auto" size="sm" :color="badgeColor(member.status)"> {{ member.status }}</base-badge>
          </div>
          <div class="tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">{{ member.email }}</div>
        </template>
        <template #phone="{ item: member }">
          <base-phone-number-field :value="member.phone" />
        </template>
        <template #dob="{ item: member }">
          <div class="tw-whitespace-nowrap">{{ formatDate(member.dob) }}</div>
        </template>
        <template #state="{ item: member }">
          <div class="tw-whitespace-nowrap">{{ member.state }}</div>
        </template>
        <template #location="{ item: member }">
          <div class="tw-flex">
            <div>
              <div class="tw-whitespace-nowrap">
                {{ lookupLocations(member.locations) }}
              </div>
            </div>
          </div>
        </template>
        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">
            No Results
          </div>
        </template>
        <template #loading-row>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-12 tw-w-12 tw-rounded-full" /></td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex">
              <base-skeleton-loader class="tw-h-3 tw-w-40" />
              <base-skeleton-loader class="tw-h-3 tw-w-12 tw-rounded-full tw-ml-3" />
            </div>
            <base-skeleton-loader class="tw-h-2 tw-w-24 tw-mt-2" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4" v-if="organization.collectDobSetting">
            <base-skeleton-loader class="tw-h-3 tw-w-20" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4">
            <base-skeleton-loader class="tw-h-3 tw-w-24" />
          </td>
          <td class="tw-py-3 tw-px-4" v-if="organization.useCreditsSetting" align="right">
            <base-skeleton-loader class="tw-h-3 tw-w-12" />
          </td>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>
    <filter-slideover ref="filterSlideOver" :businessId="businessId" @filter-updated="applyFilter"></filter-slideover>
    <create-member-plan-slide-over ref="createMemberSlideOver" :business-id="businessId" />
    <invite-member-plan-slide-over ref="inviteMemberSlideOver" :business-id="businessId" @invited="loadPage" />
  </div>
</template>
<script>
import CreateMemberPlanSlideOver from "@/views/business/member-search/components/CreateMemberPlanSlideOver";
import InviteMemberPlanSlideOver from "@/views/business/member-search/components/InviteMemberPlanSlideOver";
import FilterSlideover from "@/views/business/member-search/components/FilterSlideover";

import Avatar from "@/components/Avatar";

import { SearchIcon, ChevronDownIcon, FilterIcon } from "@/assets/svgs";
import { debounce } from "debounce";
import { mapGetters } from "vuex";
import moment from "moment";
import { capitalize, uniq, isEqual } from "lodash";
import ExportButton from "@/components/ExportButton.vue";

const TAKE_KEY = "predictile_business_members_take";

export default {
  name: "BusinessMembersView",
  components: {
    FilterSlideover,
    CreateMemberPlanSlideOver,
    InviteMemberPlanSlideOver,
    SearchIcon,
    ChevronDownIcon,
    Avatar,
    FilterIcon,
    ExportButton,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      query: null,
      isLoading: false,
      items: [],
      filters: {
        locationsFilter: [],
        statesFilter: [],
      },
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["locations"]),
    ...mapGetters("role", ["isLocationFiltered", "isPscSecondaryRole"]),
    hideCreation() {
      return (this.isLocationFiltered && this.organization.usePrescriptionProgramSetting) || this.isPscSecondaryRole;
    },
    fields() {
      let columns = [];

      columns.push({
        title: "",
        key: "avatar",
        thClass: "tw-w-0 tw-text-center",
      });
      columns.push({
        title: this.organization.customerLabelSingular,
        key: "member",
      });
      columns.push({
        title: "Phone",
        key: "phone",
      });

      if (this.organization.collectDobSetting)
        columns.push({
          title: "DOB",
          key: "dob",
        });
      columns.push({
        title: "State",
        key: "state",
      });

      columns.push({
        title: "Location",
        key: "location",
      });

      if (this.organization.useCreditsSetting === true)
        columns.push({
          title: "Credits",
          key: "creditBalance",
          align: "right",
          thClass: "tw-text-right",
        });

      return columns;
    },
    pageTitle() {
      return `${capitalize(this.organization.customerLabelPlural)}`;
    },
    newMemberButtonTitle() {
      return `New ${capitalize(this.organization.customerLabelSingular)}`;
    },
    searchPlaceholder() {
      return `Search for a ${this.organization.customerLabelSingular} using name, email, or phone number`;
    },
    countFilters() {
      var count = 0;

      if (this.filters.locationsFilter) {
        count += this.filters.locationsFilter.length;
      }

      if (this.filters.statesFilter) {
        count += this.filters.statesFilter.length;
      }
      return count;
    },
    requestPayload() {
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item;
          })
        : [];
      let statesFilterStrings = this.filters.statesFilter
        ? this.filters.statesFilter.map(function(item) {
            return item;
          })
        : [];

      return {
        LocationsFilter: locationFilterStrings,
        StatesFilter: statesFilterStrings,
      };
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query(newValue) {
      this.page.skip = 0;
      if (newValue.length !== 0 && newValue.length < 3) return;
      this.debouncedLoadPage();
    },
    filters: function(newValue, oldValue) {
      if (newValue !== oldValue) this.loadPage();
    },
  },
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    this.$refs.searchInput.focus();
  },
  methods: {
    badgeColor(status) {
      let color;
      switch (status) {
        case "Active":
          color = "green";
          break;
        case "Failed Payment":
          color = "red";
          break;
        case "Paused":
        case "Cancelled":
        case "Awaiting Payment":
        case "Deleted":
        case "New Rx":
        case "Rx Contacted":
        case "Rx Declined":
        case "Rx Accepted":
        case "Rx Changed":
          color = "gray";
          break;
        default:
          color = "gray";
      }
      return color;
    },
    lookupLocations(input) {
      return uniq(
        input
          .split(",")
          .map((l) => {
            const location = this.locations.find((loc) => loc.businessLocationId === l);
            return location ? location.name : null;
          })
          .filter((location) => location),
      ).join(", ");
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    view(userId) {
      if (this.organization.usePrescriptionProgramSetting) {
        window.open(`/businesses/${this.businessId}/members/${userId}/prescriptions`, "_blank");
      } else {
        window.open(`/businesses/${this.businessId}/members/${userId}/plans`, "_blank");
      }
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/members/search/?skip=${this.page.skip}&take=${this.page.take}`;
      let filters = {
        locationsFilter: [],
        statesFilter: [],
      };
      if (this.filters !== undefined) filters = this.filters;

      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;
      this.$axios
        .post(url, {
          locationsFilter: filters.locationsFilter,
          statesFilter: filters.statesFilter,
        })
        .then((res) => {
          this.isLoading = false;
          this.items = res.data.items;
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load ${this.organization.customerLabelPlural}`,
            message: `We had a problem retrieving the list of ${this.organization.customerLabelPlural}. Please reload the page to try again.`,
            error: err,
          });
        });
    },
    createMemberPlan() {
      this.$refs.createMemberSlideOver.show();
    },
    inviteMemberPlan() {
      this.$refs.inviteMemberSlideOver.show();
    },
    formatDate(date) {
      if (!date) return "-";
      return new moment.utc(date).local().format("MM/DD/YYYY");
    },
    applyFilter(filters) {
      if (!isEqual(this.filters, filters)) {
        this.filters = filters;
        this.loadPage();
      } else {
        this.filters = filters;
      }
    },
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the list of patients. You may want to reload the page to try again.",
        error: err,
      });
    },
  },
};
</script>
